import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import createMarkup from "helpers/createMarkup";
import Section from "components/common/Section";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import logo from "assets/img/logo/mwanLogo-white.png";
import styles from "./landing.module.css";
import { useTranslation } from "react-i18next";
import { faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { getAllDatabaseRecords } from "components/workflow/WorkflowHelper";
import Divider from "components/common/Divider";
import { GetSocialLinks } from "common/general";

const FooterTitle = ({ children, classes }) => <h5 className={`text-uppercase text-white opacity-85 mb-3 ${classes}`}>{children}</h5>;

FooterTitle.propTypes = { children: PropTypes.node.isRequired };

const FooterList = ({ list }) => (
	<ul className="list-unstyled">
		{list.map(({ title, to, isDownload = false, openInNewTab = false }, index) => (
			<li className="mb-1" key={index}>
				{isDownload ? (
					<a className={`${styles.footerLink}`} target="_blank" href={to} download>
						{title}
					</a>
				) : (
					<Link className={` ${styles.footerLink}`} to={to} target={openInNewTab ? "_blank" : undefined}>
						{title}
					</Link>
				)}
			</li>
		))}
	</ul>
);

FooterList.propTypes = { list: PropTypes.array.isRequired };
const isAPK = /wv|WebView/i.test(navigator.userAgent) || typeof window.ReactNativeWebView !== "undefined";
const FooterBlogList = ({ list }) => (
	<ul className="list-unstyled">
		{list.map((blog, index) => (
			<li key={index}>
				<h5 className="fs-0 mb-0">
					<Link className="text-600" to="#!">
						{blog.title}
					</Link>
				</h5>
				<p className="text-600 opacity-50">
					{blog.date} &bull; {blog.read} read {blog.star && <span dangerouslySetInnerHTML={createMarkup("&starf;")} />}
				</p>
			</li>
		))}
	</ul>
);
FooterBlogList.propTypes = { list: PropTypes.array.isRequired };
const Footer = () => {
	const [voices, setVoices] = useState([]);
	const [readingEnabled, setReadingEnabled] = useState(JSON.parse(localStorage.getItem("readingEnabled")) || false);
	const [isRTL, setIsRTL] = useState(JSON.parse(localStorage.getItem("isRTL")) || false);
	const toggleReading = () => {
		setReadingEnabled((prev) => {
			const newValue = !prev;
			localStorage.setItem("readingEnabled", JSON.stringify(newValue));
			if (!newValue) window.speechSynthesis.cancel();
			return newValue;
		});
	};
	useEffect(() => {
		const loadVoices = () => {
			const availableVoices = window.speechSynthesis.getVoices();
			setVoices(availableVoices);
		};

		loadVoices();
		window.speechSynthesis.onvoiceschanged = loadVoices;

		const handleClick = (e) => {
			if (!readingEnabled) return;
			const text = e.target.innerText?.trim();
			if (!text) return;

			if (window.speechSynthesis) {
				window.speechSynthesis.cancel();

				const utterance = new SpeechSynthesisUtterance(text);

				// detect if the text has Arabic characters
				const isArabic = /[\u0600-\u06FF]/.test(text);

				utterance.lang = isArabic ? "ar-SA" : "en-US";

				// choose a matching voice if available
				if (voices.length > 0) {
					const selectedVoice = voices.find((v) => v.lang.startsWith(isArabic ? "ar" : "en")) || voices[0];
					utterance.voice = selectedVoice;
				}

				utterance.rate = 1;
				utterance.pitch = 1;

				window.speechSynthesis.speak(utterance);
			}
		};

		document.addEventListener("click", handleClick);
		return () => {
			document.removeEventListener("click", handleClick);
		};
	}, [readingEnabled]);
	const [t, i18n] = useTranslation();
	const scrollToTop = () => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth",
		});
	};
	const footerlist1 = [
		{ title: t("الرؤية والرسالة"), to: "https://mwan.gov.sa/vision-mission", openInNewTab: true },
		{ title: t("أهداف المركز"), to: "https://mwan.gov.sa/goals", openInNewTab: true },
		{ title: t("مهام المركز"), to: "https://mwan.gov.sa/tasks", openInNewTab: true },
		{ title: t("قيم المركز"), to: "https://mwan.gov.sa/values", openInNewTab: true },
	];
	const footerlist2 = [
		{ title: t("منصة التراخيص والتصاريح"), to: "https://mwan.gov.sa/permit-and-license-platform", openInNewTab: true },
		{ title: t("الاشتراطات والمتطلبات"), to: "https://mwan.gov.sa/conditions-and-requirements", openInNewTab: true },
	];
	const footerlist3 = [
		{ title: t("وزارة البيئة والمياه والزراعة"), to: "https://www.mewa.gov.sa/ar/Pages/default.aspx", openInNewTab: true },
		{ title: t("صندوق البيئة"), to: "https://ef.gov.sa/ar/Pages/default.aspx", openInNewTab: true },
		{ title: t("دليل المستخدم"), to: "/manuals", openInNewTab: false },
	];
	const [socialmedia, setSocialmedia] = useState([]);

	const [zoomLevel, setZoomLevel] = useState(100);

	useEffect(() => {
		document.body.style.zoom = `${zoomLevel}%`;
		return () => {
			// Clean up effect to prevent memory leaks
			document.body.style.zoom = "100%";
		};
	}, [zoomLevel]);
	const zoomIn = () => setZoomLevel((prev) => Math.min(prev + 10, 300)); // max 300%
	const zoomOut = () => setZoomLevel((prev) => Math.max(prev - 10, 50)); // min 50%
	const handleReset = () => setZoomLevel(100);
	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await GetSocialLinks();

				if (response?.data?.dataBaseContent) {
					// Map dataBaseContent to the desired structure
					const mappedData = response.data.dataBaseContent.map((item) => ({
						id: item.id,
						link: item.link,
						icon: item.icon,
					}));
					// Update state with the fetched and mapped data
					setSocialmedia((prev) => [...prev, ...mappedData]); // Append new data to existing
				} else {
					console.error("Invalid response structure:", response);
				}
			} catch (error) {
				console.error("Error fetching social data:", error);
			}
		};

		fetchData();
	}, []);
	return (
		<>
			<Section
				className={`pt-6 pb-4 light ${styles.backgroundFooter} ${window.location.href.includes("inspection") || window.location.href.includes("local") || isAPK ? styles.appmode : ""}`}
			>
				<Row className="mb-3">
					<Col lg={3} xs={12} md={3} className="mb-3">
						<FooterTitle>
							<Link className={`${styles.footerLink}`} to={"/aboutus"}>
								{t("Overview")}
							</Link>
						</FooterTitle>
						<Divider />
						<FooterList list={footerlist1} />
					</Col>
					<Col lg={3} xs={12} md={3} className="mb-3">
						<FooterTitle>
							<Link className={`${styles.footerLink}`} to={"/manuals"}>
								{t("نظام التراخيص")}
							</Link>
						</FooterTitle>
						<Divider />
						<FooterList list={footerlist2} />
					</Col>
					<Col lg={3} xs={12} md={3} className="mb-3">
						<FooterTitle>
							<Link className={`${styles.footerLink}`} to={"/manuals"}>
								{t("روابط هامة")}
							</Link>
						</FooterTitle>
						<Divider />
						<FooterList list={footerlist3} />
					</Col>
					<Col lg={3} xs={12} md={3}>
						<FooterTitle>{t("Follow us")}</FooterTitle>
						<Divider />
						<div className="icon-group">
							{socialmedia.map((item) => (
								<a key={item.id} href={item.link} className={isRTL ? styles.socialBoxesAr : styles.socialBoxes} target="_blank">
									<FontAwesomeIcon icon={item.icon.toLowerCase().includes("twitter") ? faXTwitter : "fa-brands fa-" + item.icon} className={styles.footerIcon} />
								</a>
							))}
						</div>
						<FooterTitle classes="mt-3">{t("امكانية الوصول")}</FooterTitle>
						<Divider />
						<span className={isRTL ? styles.socialBoxesMicAr : styles.socialBoxesMic} onClick={toggleReading}>
							{readingEnabled ? (
								<svg width="30px" height="30px" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" strokeWidth="3" stroke="#ffffff" fill="none">
									<g id="SVGRepo_bgCarrier" strokeWidth="0" />

									<g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" stroke="#CCCCCC" strokeWidth="0.256" />

									<g id="SVGRepo_iconCarrier">
										<path d="M47.67,28.43v3.38a15.67,15.67,0,0,1-31.34,0V28.43" strokeLinecap="round" />

										<rect x="22.51" y="6.45" width="18.44" height="34.22" rx="8.89" strokeLinecap="round" />

										<line x1="31.73" y1="57.34" x2="31.73" y2="47.71" strokeLinecap="round" />

										<line x1="37.14" y1="57.55" x2="26.43" y2="57.55" strokeLinecap="round" />
									</g>
								</svg>
							) : (
								<svg width="30px" height="30px" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" strokeWidth="3" stroke="#ffffff" fill="none">
									<g id="SVGRepo_bgCarrier" strokeWidth="0" />

									<g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />

									<g id="SVGRepo_iconCarrier">
										<path d="M47.67,28.43v3.38a15.67,15.67,0,0,1-31.34,0V28.43" strokeLinecap="round" />

										<path d="M41.07,24.44v7.34a8.89,8.89,0,0,1-8.89,8.89h-.65a5.66,5.66,0,0,1-1.31-.09" strokeLinecap="round" />

										<path d="M23.56,35.73a9,9,0,0,1-.92-3.95V15.34a8.89,8.89,0,0,1,8.89-8.89h.65a8.9,8.9,0,0,1,8,5" strokeLinecap="round" />

										<line x1="31.73" y1="57.34" x2="31.73" y2="47.71" strokeLinecap="round" />

										<line x1="37.14" y1="57.55" x2="26.43" y2="57.55" strokeLinecap="round" />

										<line x1="49" y1="6.99" x2="16.33" y2="52.53" strokeLinecap="round" />
									</g>
								</svg>
							)}
						</span>
						<span className={isRTL ? styles.socialBoxesMicAr : styles.socialBoxesMic} onClick={zoomIn}>
							<svg fill="#ffffff" width="30px" height="30px" viewBox="0 0 32 32" id="icon" xmlns="http://www.w3.org/2000/svg" stroke="transparent">
								<g id="SVGRepo_bgCarrier" strokeWidth="0" />
								<g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />
								<g id="SVGRepo_iconCarrier">
									{" "}
									<defs>
										<style>{`.cls-1 { fill: none; }`}</style>
									</defs>{" "}
									<polygon points="24 19 21 19 21 16 19 16 19 19 16 19 16 21 19 21 19 24 21 24 21 21 24 21 24 19" />{" "}
									<path d="M31,29.5859l-4.6885-4.6884a8.028,8.028,0,1,0-1.414,1.414L29.5859,31ZM20,26a6,6,0,1,1,6-6A6.0066,6.0066,0,0,1,20,26Z" />{" "}
									<path d="M4,8H2V4A2.0021,2.0021,0,0,1,4,2H8V4H4Z" /> <path d="M26,8H24V4H20V2h4a2.0021,2.0021,0,0,1,2,2Z" /> <rect x="12" y="2" width="4" height="2" />{" "}
									<path d="M8,26H4a2.0021,2.0021,0,0,1-2-2V20H4v4H8Z" /> <rect x="2" y="12" width="2" height="4" />{" "}
									<rect id="_Transparent_Rectangle_" data-name="&lt;Transparent Rectangle&gt;" className="cls-1" width="32" height="32" />{" "}
								</g>
							</svg>
						</span>
						<span className={isRTL ? styles.socialBoxesMicAr : styles.socialBoxesMic} onClick={zoomOut}>
							<svg fill="#ffffff" width="30px" height="30px" viewBox="0 0 32 32" id="icon" xmlns="http://www.w3.org/2000/svg" stroke="transparent">
								<g id="SVGRepo_bgCarrier" strokeWidth="0" />

								<g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />

								<g id="SVGRepo_iconCarrier">
									{" "}
									<defs>
										<style>{`.cls-1 { fill: none; }`}</style>
									</defs>{" "}
									<rect x="16" y="19" width="8" height="2" />{" "}
									<path d="M31,29.5859l-4.6885-4.6884a8.028,8.028,0,1,0-1.414,1.414L29.5859,31ZM20,26a6,6,0,1,1,6-6A6.0066,6.0066,0,0,1,20,26Z" />{" "}
									<path d="M4,8H2V4A2.0021,2.0021,0,0,1,4,2H8V4H4Z" /> <path d="M26,8H24V4H20V2h4a2.0021,2.0021,0,0,1,2,2Z" /> <rect x="12" y="2" width="4" height="2" />{" "}
									<path d="M8,26H4a2.0021,2.0021,0,0,1-2-2V20H4v4H8Z" /> <rect x="2" y="12" width="2" height="4" />{" "}
									<rect id="_Transparent_Rectangle_" data-name="&lt;Transparent Rectangle&gt;" className="cls-1" width="32" height="32" />{" "}
								</g>
							</svg>
						</span>
						{zoomLevel != "100" && (
							<span className={isRTL ? styles.socialBoxesMicAr : styles.socialBoxesMic} onClick={handleReset}>
								<svg width="30px" height="30px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#ffffff" stroke="#ffffff">
									<g id="SVGRepo_bgCarrier" strokeWidth="0" />

									<g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />

									<g id="SVGRepo_iconCarrier">
										{" "}
										<defs>
											<style>{`.c{stroke:#fffffffff;}.c,.d,.e,.f{fill:none;stroke-linecap:round;stroke-linejoin:round;}.d,.e,.f{stroke:#fffffffffff;}.e{stroke-dasharray:0 0 3 3;}.f{stroke-dasharray:0 0 3.34 3.34;}`}</style>{" "}
										</defs>{" "}
										<g id="a" />{" "}
										<g id="b">
											{" "}
											<g>
												{" "}
												<polyline className="d" points="7.99 19.5 2.5 19.5 2.5 16.5" /> <line className="e" x1="2.5" x2="2.5" y1="13.5" y2="9" />{" "}
												<polyline className="d" points="2.5 7.5 2.5 4.5 5.5 4.5" /> <line className="f" x1="8.83" x2="13.84" y1="4.5" y2="4.5" />{" "}
												<polyline className="d" points="15.5 4.5 18.5 4.5 18.5 7" />{" "}
											</g>{" "}
											<path className="c" d="M18.8,13.39c0,2.98-2.42,5.4-5.4,5.4s-5.4-2.42-5.4-5.4,2.42-5.4,5.4-5.4,5.4,2.42,5.4,5.4Z" />{" "}
											<line className="c" x1="17.35" x2="21.5" y1="17.31" y2="21.5" />{" "}
										</g>{" "}
									</g>
								</svg>
							</span>
						)}
					</Col>
				</Row>
				<Row className="mt-5 mt-lg-0">
					<Col lg={9} className="mb-3">
						<Row>
							<Col xs={12} ms={12} md={3}>
								<Link className={`${styles.footerLink}`} to={"/privacypolicy"}>
									{t("Privacy policy")}
								</Link>
							</Col>
							<Col xs={12} ms={12} md={3}>
								<Link className={` ${styles.footerLink}`} to={"/faq"}>
									{t("FAQ")}
								</Link>
							</Col>
							<Col xs={12} ms={12} md={3}>
								<Link className={` ${styles.footerLink}`} to={"/contact"}>
									{t("Contact us")}
								</Link>
							</Col>
							<Col xs={12} ms={12} md={12}>
								<div className={`mt-3 ${styles.footerLink}`}>{t("All rights reserved 2025 National Center for Waste Management | Kingdom of Saudi Arabia")}</div>
							</Col>
						</Row>
					</Col>

					<Col lg={3} className={`mb-3 pt-2`}>
						<img src={logo} className={`${styles.logosizeFOOTER}`} />
					</Col>
				</Row>
			</Section>
		</>
	);
};

export default Footer;
